import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Search, X, LayoutGrid, List as ListIcon, Plus, SlidersHorizontal,
  Loader2, FileQuestion, Sparkles,
} from 'lucide-react';
import { usePermission } from '../../../context/PermissionContext';
import { sopApi } from './api';
import SopCard from './components/SopCard';
import TagChip from './components/TagChip';

const PAGE_SIZE = 24;

const SORTS = [
  { value: 'recent', label: 'Recently updated' },
  { value: 'title', label: 'Title A–Z' },
  { value: 'popular', label: 'Most viewed' },
  { value: 'oldest', label: 'Oldest first' },
];

const EXAMPLES = [
  'how do I check in a trailer',
  'cycle count',
  'forklift inspection',
];

/**
 * @param mode 'all' | 'favorites' — 'recent' has its own endpoint and screen.
 */
const SopLibrary = ({ mode = 'all' }) => {
  const { hasPermission } = usePermission();
  const canAuthor = hasPermission('ksp_sops_author') || hasPermission('ksp_sops_manage');

  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [sort, setSort] = useState('recent');
  const [view, setView] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);

  const [allTags, setAllTags] = useState([]);
  const [result, setResult] = useState({ documents: [], total: 0, suggestedTags: [], usedFallback: false });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [offset, setOffset] = useState(0);

  // Guards against a slow early request overwriting a newer one's results.
  const requestSeq = useRef(0);

  // Typing shouldn't fire a query per keystroke; 300ms is short enough that the
  // list still feels live.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    sopApi.listTags()
      .then((r) => setAllTags(r.data || []))
      .catch(() => setAllTags([]));
  }, []);

  const params = useMemo(() => ({
    q: debouncedQuery || undefined,
    tags: selectedTags.map((t) => t.id),
    sort: debouncedQuery ? undefined : sort, // a scored search orders itself
    favorites: mode === 'favorites' ? 'true' : undefined,
    limit: PAGE_SIZE,
  }), [debouncedQuery, selectedTags, sort, mode]);

  const load = useCallback(async (nextOffset, append) => {
    const seq = requestSeq.current + 1;
    requestSeq.current = seq;

    if (append) setLoadingMore(true); else setLoading(true);
    setError('');

    try {
      const res = await sopApi.list({ ...params, offset: nextOffset });
      if (requestSeq.current !== seq) return; // a newer request already won
      setResult((prev) => ({
        ...res.data,
        documents: append ? [...prev.documents, ...res.data.documents] : res.data.documents,
      }));
      setOffset(nextOffset);
    } catch (err) {
      if (requestSeq.current === seq) setError(err.message || 'Failed to load SOPs');
    } finally {
      if (requestSeq.current === seq) { setLoading(false); setLoadingMore(false); }
    }
  }, [params]);

  useEffect(() => { load(0, false); }, [load]);

  const toggleTag = (tag) => {
    setSelectedTags((prev) =>
      prev.some((t) => t.id === tag.id) ? prev.filter((t) => t.id !== tag.id) : [...prev, tag]);
  };

  // Attribute the click so Search Insights can tell "found it" from "gave up".
  const handleOpen = (doc) => {
    if (!result.searchLogId) return;
    const rank = result.documents.findIndex((d) => d.id === doc.id) + 1;
    sopApi.logSearchClick({ searchLogId: result.searchLogId, documentId: doc.id, rank }).catch(() => {});
  };

  const hasFilters = selectedTags.length > 0 || !!debouncedQuery;
  const canLoadMore = result.documents.length < result.total;

  const topTags = useMemo(() => {
    const selectedIds = new Set(selectedTags.map((t) => t.id));
    // Selected chips stay pinned so they never scroll out of the rail.
    const rest = allTags.filter((t) => !selectedIds.has(t.id)).slice(0, 14);
    return [...selectedTags, ...rest];
  }, [allTags, selectedTags]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-100">
            {mode === 'favorites' ? 'Favorite SOPs' : 'Standard Operating Procedures'}
          </h2>
          <p className="text-sm text-slate-400 mt-1">
            Search by name, or just describe what you&rsquo;re trying to do.
          </p>
        </div>
        {canAuthor && (
          <Link
            to="/tiles/ksp_sops/new"
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700
              text-white rounded-md text-sm font-medium transition flex-shrink-0"
          >
            <Plus className="w-4 h-4" />
            New SOP
          </Link>
        )}
      </div>

      {/* --- search --- */}
      <div className="space-y-3">
        <div className="relative">
          <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="e.g. how do I check in an inbound trailer?"
            className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-12 pr-24 py-3.5
              text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="p-1.5 rounded-md text-slate-400 hover:text-slate-100 hover:bg-slate-700"
                aria-label="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowFilters((v) => !v)}
              className={`p-1.5 rounded-md transition ${showFilters
                ? 'bg-indigo-600 text-white'
                : 'text-slate-400 hover:text-slate-100 hover:bg-slate-700'}`}
              aria-label="Toggle filters"
            >
              <SlidersHorizontal className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* tag rail — the fastest way to narrow without typing */}
        {topTags.length > 0 && (
          <div className="flex flex-wrap gap-2 items-center">
            {topTags.map((tag) => (
              <TagChip
                key={tag.id}
                tag={tag}
                size="xs"
                count={tag.usageCount}
                selected={selectedTags.some((t) => t.id === tag.id)}
                onClick={toggleTag}
              />
            ))}
            {selectedTags.length > 0 && (
              <button
                type="button"
                onClick={() => setSelectedTags([])}
                className="text-xs text-slate-400 hover:text-slate-200 underline ml-1"
              >
                Clear tags
              </button>
            )}
          </div>
        )}

        {showFilters && (
          <div className="flex flex-wrap items-center gap-3 p-3 bg-slate-800/60 border border-slate-700 rounded-lg">
            <label className="text-xs text-slate-400" htmlFor="sop-sort">Sort</label>
            <select
              id="sop-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              disabled={!!debouncedQuery}
              className="bg-slate-900 border border-slate-700 rounded-md px-3 py-1.5 text-sm
                text-slate-200 disabled:opacity-50"
            >
              {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            {debouncedQuery && (
              <span className="text-xs text-slate-500">Sorted by relevance while searching</span>
            )}
            <div className="ml-auto flex items-center gap-1">
              {[['grid', LayoutGrid], ['list', ListIcon]].map(([key, Icon]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setView(key)}
                  className={`p-1.5 rounded-md ${view === key
                    ? 'bg-indigo-600 text-white'
                    : 'text-slate-400 hover:text-slate-100 hover:bg-slate-700'}`}
                  aria-label={`${key} view`}
                >
                  <Icon className="w-4 h-4" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* --- results --- */}
      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Loading SOPs…
        </div>
      ) : error ? (
        <div className="p-4 bg-rose-500/10 border border-rose-500/30 rounded-lg text-rose-300 text-sm">
          {error}
        </div>
      ) : result.documents.length === 0 ? (
        <div className="text-center py-16 space-y-4">
          <FileQuestion className="w-12 h-12 text-slate-600 mx-auto" />
          {hasFilters ? (
            <>
              <div>
                <p className="text-slate-300 font-medium">No SOPs match that.</p>
                <p className="text-sm text-slate-500 mt-1">
                  Try fewer words, or browse by tag instead.
                </p>
              </div>
              {result.suggestedTags?.length > 0 && (
                <div className="flex flex-wrap gap-2 justify-center items-center">
                  <span className="text-xs text-slate-400">Related tags:</span>
                  {result.suggestedTags.map((t) => (
                    <TagChip key={t.id} tag={t} size="xs" onClick={() => { setQuery(''); toggleTag(t); }} />
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-2 justify-center items-center pt-2">
                <span className="text-xs text-slate-500">Try:</span>
                {EXAMPLES.map((ex) => (
                  <button
                    key={ex}
                    type="button"
                    onClick={() => setQuery(ex)}
                    className="text-xs px-2.5 py-1 rounded-full bg-slate-800 border border-slate-700
                      text-slate-300 hover:border-indigo-500/60"
                  >
                    {ex}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div>
              <p className="text-slate-300 font-medium">
                {mode === 'favorites' ? 'No favorites yet.' : 'No SOPs yet.'}
              </p>
              <p className="text-sm text-slate-500 mt-1">
                {mode === 'favorites'
                  ? 'Star an SOP from its page to pin it here.'
                  : canAuthor ? 'Add the first one to get started.' : 'Check back once your team adds some.'}
              </p>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between text-sm text-slate-400">
            <span>
              {result.total} {result.total === 1 ? 'SOP' : 'SOPs'}
              {debouncedQuery && <> for &ldquo;{debouncedQuery}&rdquo;</>}
            </span>
            {result.usedFallback && (
              <span className="inline-flex items-center gap-1.5 text-xs text-amber-300">
                <Sparkles className="w-3.5 h-3.5" />
                Showing close matches
              </span>
            )}
          </div>

          <div className={view === 'grid'
            ? 'grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
            : 'space-y-3'}
          >
            {result.documents.map((doc) => (
              <SopCard key={doc.id} doc={doc} view={view} onOpen={handleOpen} />
            ))}
          </div>

          {canLoadMore && (
            <div className="flex justify-center pt-2">
              <button
                type="button"
                onClick={() => load(offset + PAGE_SIZE, true)}
                disabled={loadingMore}
                className="px-5 py-2 bg-slate-800 border border-slate-700 hover:border-indigo-500/60
                  rounded-md text-sm text-slate-200 disabled:opacity-50 inline-flex items-center gap-2"
              >
                {loadingMore && <Loader2 className="w-4 h-4 animate-spin" />}
                Load more
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SopLibrary;
